import type { ReactElement } from 'react'
import type { Entry } from 'contentful'
import React, { useCallback } from 'react'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import ArticleHeader from '@/components/article-header'
import ArticleBreadcrumb from '@/components/article-breadcrumb'
import PageWrapper from '@/components/page-wrapper'
import RichText from '@/components/ui/rich-text'
import Quote from '@/components/ui/quote'
import Callout from '@/components/ui/resource/callout'
import ComponentImage from '@/components/ui/image'
import PrintWrapper from '@/components/print-wrapper'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import SectionCta from '@/components/section/cta'
import { SetOfPricingComponent } from '@/components/section/set-of-pricing'
import SectionTabSection from '@/components/section/tab-section'
import colors from '@/constants/colors'
import { SetOfCardsSectionComponent } from '@/components/section/set-of-cards'
import { usePage } from '@inertiajs/react'
import Table from '@/components/table'
import Routes from '@/constants/routes'

import {
  isTypeComponentQuote,
  isTypeComponentTable,
  isTypeResourceCallout,
  isTypeResourceContent,
  isTypeResourceImage,
  isTypeSectionCta,
  isTypeSectionSetOfCards,
  isTypeSectionSetOfPricing,
  type TypeSeo,
  type TypeResourcePage,
} from '@/types/ctf'

type WrapperProps = {
  children: ReactElement
}

type ResourceNewProps = {
  post: TypeResourcePage<'WITHOUT_UNRESOLVABLE_LINKS'>
  defaultExtraContent: any
  headerNav: any[]
  footerNav: any[]
  headerBanners: any[]
  newsletterForm: any
}

export default function ResourcesNew(props: ResourceNewProps): React.JSX.Element {
  const { post, defaultExtraContent, headerNav, footerNav, headerBanners, newsletterForm } = props

  const {
    content,
    description,
    downloadAsPdf,
    heroButtons,
    image,
    imageGradientOverlay,
    pageWidth,
    seo: _seo,
    title,
    extraContent,
    extraContentPosition = 'Bottom',
    publishDate,
    thirdPartyLogo,
    resourceTypes,
  } = post.fields
  const seo = _seo || ({ fields: { title, description, ogImage: image } } as TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>)
  if (!seo.fields?.title) seo.fields.title = title
  if (!seo.fields?.description) seo.fields.description = description
  if (!seo.fields?.ogImage) seo.fields.ogImage = image

  const { url } = usePage()

  const isPrintView = isPrint()
  const pathname = url.split('?')[0]

  const hasCallouts = content?.some((section) => {
    return (
      section &&
      isTypeResourceContent(section) &&
      Array.isArray(section.fields.callouts) &&
      section.fields.callouts?.length > 0
    )
  })

  const breadcrumb = [{ name: 'Resources', path: '/resources/' }, { name: title }]

  const CheckPricingExtraContent = (obj: Entry | undefined) => obj && isTypeSectionSetOfPricing(obj)
  const getPublishDate = () => {
    if (publishDate && resourceTypes?.find((type) => type?.fields.name === 'Newsletters')) {
      return publishDate
    }
  }

  const renderExtraContent = () => {
    return extraContent?.map((section) => {
      if (!section) return null
      if (isTypeSectionSetOfPricing(section)) {
        return (
          <SetOfPricingComponent
            key={section.sys.id}
            {...section.fields}
            internalTitle={section.fields.internalTitle || section.sys.id}
          />
        )
      }

      if (isTypeSectionCta(section))
        return (
          <SectionCta
            key={section.sys.id}
            {...section.fields}
            internalName={section.fields.internalName || section.sys.id}
          />
        )
      return null
    })
  }

  const isBitwardenSecurityPerspectives = !!resourceTypes?.find((type) => type?.fields.slug === 'security-perspectives')

  const Wrapper = useCallback(
    ({ children }: WrapperProps) =>
      isPrintView ? (
        <PrintWrapper
          title={title}
          description={description}
          section={'Resource Center'}
          thirdPartyLogo={thirdPartyLogo}
          pageUrl={Routes.hostname + pathname}
          securityPerspectives={isBitwardenSecurityPerspectives}
          coverImage={isBitwardenSecurityPerspectives ? image : undefined}
        >
          {children}
        </PrintWrapper>
      ) : (
        <PageWrapper
          seo={seo}
          breadcrumb={breadcrumb}
          headerNav={headerNav}
          footerNav={footerNav}
          headerBanners={headerBanners}
          newsletterForm={newsletterForm}
        >
          <ArticleHeader
            image={image}
            imageOverlay={imageGradientOverlay}
            publishDate={getPublishDate()}
            title={title}
            description={description}
            tagline={isBitwardenSecurityPerspectives ? 'Bitwarden Security Perspectives' : 'Bitwarden Resources'}
            downloadAsPdf={downloadAsPdf}
            unconstrained
            backgroundColor={colors.indigoBlue}
            heroButtons={heroButtons}
          />

          <div className="bg-secondary-100 dark:bg-transparent py-8 lg:py-20">
            {extraContent && <div className="mb-8">{extraContentPosition === 'Top' && renderExtraContent()}</div>}
            {children}
          </div>
          <div className="my-10 lg:my-20 [&_section]:mb-10 lg:[&_section]:mb-20 [&_section]:mt-10">
            {extraContentPosition === 'Above Pricing' && renderExtraContent()}
            {((extraContent && !extraContent.some(CheckPricingExtraContent)) || !extraContent) && (
              <SectionTabSection {...defaultExtraContent.fields} />
            )}
            {(!extraContentPosition || extraContentPosition === 'Bottom') && renderExtraContent()}
          </div>
        </PageWrapper>
      ),
    [isPrintView]
  )

  const renderCallout = (section) => (
    <div className="flex space-y-5 md:space-y-8 flex-col pb-6">
      {section.fields.callouts?.map((callout) => {
        if (!callout) return null
        if (isTypeComponentQuote(callout)) {
          return <Quote key={callout.sys.id} {...callout.fields} />
        }
        if (isTypeResourceCallout(callout)) {
          return <Callout key={callout.sys.id} contentful_id={callout.sys.id} {...callout.fields} />
        }
        if (isTypeResourceImage(callout)) {
          return <ComponentImage key={callout.sys.id} {...callout.fields} />
        }
        return <div>unsupported node</div>
      })}
    </div>
  )

  const testForCallout = (section) => {
    return section.fields.callouts?.length && section.fields.callouts.length >= 1
  }

  return (
    <Wrapper>
      <div
        className={cx(
          'mx-auto',
          (!pageWidth || pageWidth === 'Normal') && `max-w-8xl`,
          pageWidth === 'Full' && `max-w-8xl`,
          pageWidth === 'Tight' && `max-w-6xl`,
          !isPrintView && 'px-5 lg:px-9'
        )}
      >
        <div className={cx('bg-white dark:bg-secondary-900 rounded-4xl', !isPrintView && `p-6 lg:p-10 shadow-lg`)}>
          {!isPrintView && <ArticleBreadcrumb breadcrumb={breadcrumb} />}

          {content?.map((section) => {
            if (!section) return null
            if (isTypeResourceContent(section)) {
              return (
                <div key={section.sys.id}>
                  <div className="break-inside-avoid">
                    {isPrintView ? (
                      <table>
                        <tbody>
                          <tr>
                            <td valign="top" className="p-0!" width={testForCallout(section) ? '70%' : '100%'}>
                              {section.fields.title && <h2>{section.fields.title}</h2>}
                              <RichText body={section.fields.body} />
                            </td>
                            {testForCallout(section) && (
                              <td valign="top" className="pr-0! pl-8!">
                                {renderCallout(section)}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="grid grid-cols-12 gap-0 md:gap-8 mb-4">
                        <div className={cx(hasCallouts ? `col-span-12 md:col-span-8` : 'col-span-12')}>
                          <HeadingWithAnchor type="h2" title={section.fields.title} />
                          <RichText body={section.fields.body} />
                        </div>
                        {hasCallouts && (
                          <div className={cx('break-before-avoid-page col-span-12 md:col-span-4')}>
                            {renderCallout(section)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            if (isTypeSectionSetOfCards(section)) {
              return (
                <div
                  className={cx(
                    '&>section]:mb-10 lg:[&>section]:mb-20 [&>section]:mt-10 break-after-avoid-page',
                    isPrintView &&
                      '[*]:bg-transparent! [&>section]:mb-20! [.set-of-cards-container]:(px-0! block!) [.card-container]:px-0!'
                  )}
                >
                  <SetOfCardsSectionComponent key={section.sys.id} {...section.fields} />
                </div>
              )
            }
            if (isTypeComponentTable(section)) {
              return (
                <div
                  key={section.sys.id}
                  className={cx(
                    `break-inside-avoid`,
                    ...(isPrintView
                      ? [
                          `**:bg-transparent! [&_table *]:!text-xs [&_th,&_td]:p-[1rem 0.5rem]`,
                          `[&_.table-header-cell-container]:gap-2 [&_.table-header-icon]:w-8 [&_.table-header-icon]:h-8 [&_.table-header-icon svg]:!fill-none`,
                        ]
                      : [])
                  )}
                >
                  <Table {...section.fields} />
                </div>
              )
            }
            return null
          })}
        </div>
      </div>
    </Wrapper>
  )
}
